import ACLStore from "lib/aclStore";

/**
 * @description Checks if the worksheet assign modal V2 is enabled.
 * @changes Stores security mode (lock screen/safe browser) and timed test duration at per-class level instead of globally in assignment settings.
 */
export const getIsWorksheetAssignModalV2Enabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetAssignModalV2");
};

/**
 * @description Checks if the V2 mutation operations for worksheet data modifications are enabled.
 * @changes Switches GraphQL mutations to V2 schema for creating/updating worksheet data (e.g. uses `item.id` instead of `id`).
 */
export const isWorksheetMutationsV2Enabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetMutationsV2");
};

/**
 * @description Checks if the V2 query operations for fetching worksheet data are enabled. Disabled for Toddle Library portal and internal tools.
 * @changes Enables V2 GraphQL queries for fetching worksheet data with updated schema structure.
 */
export const isWorksheetQueriesV2Enabled = (
  isToddleLibraryPortal = false,
  isFromInternalTool = false
) => {
  return (
    !isToddleLibraryPortal &&
    !isFromInternalTool &&
    ACLStore.can("FeatureFlag:WorksheetQueriesV2")
  );
};

/**
 * @description Checks if the worksheet monitoring sidebar with real-time student progress tracking is enabled.
 * @changes Adds monitoring sidebar with real-time student progress tracking and switches to V2 timer component for live timer display.
 */
export const getIsWorksheetMonitoringEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetMonitoringV1");
};

/**
 * @description Checks if the worksheet external layout update is enabled.
 * @changes Moves Evaluation insights, Question details, and Other details into sidebar. Adds timer for scheduled worksheets. Moves "Send reminder" into more actions menu.
 */
export const getIsWorksheetLayoutUpdateEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetLayoutUpdate");
};

/**
 * @description Checks if the core worksheets feature is enabled.
 * @changes Enables worksheets as an assessment type in the classroom platform, allowing creation and access to worksheet assessments.
 */
export const getIsWorksheetsEnabled = () => {
  return ACLStore.can("FeatureFlag:ClassroomWorksheet");
};

/**
 * @description Checks if the revamped learning goals feature for worksheets is enabled.
 * @changes Updates the learning goals UI in worksheet questions, changing how learning goals are displayed and selected in question fields.
 */
export const getIsLearningGoalsRevampEnabled = () => {
  return ACLStore.can("FeatureFlag:LearningGoalsRevamp");
};

/**
 * @description Checks if the new MCQ settings V2 is enabled.
 * @changes Adds "Allow multiple selection" and "Additional field" options in MCQ question settings.
 */
export const getIsMCQNewSettingsEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetMCQSettingsV2");
};

/**
 * @description Checks if AI features in worksheet creation are enabled.
 * @changes Shows AI panel icons (AI Question Assistant, AI Assessment Tool) in worksheet creation. Hides them and skips worksheet create modal when disabled.
 */
export const getIsWorksheetAIEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetAIAddon");
};

/**
 * @description Checks if the new document editor for worksheets is enabled.
 * @changes Replaces standard text fields with rich document editor for worksheet instructions, answer notes, question titles/headers, MCQ options, and additional text fields.
 *          Also enables usage of rich text capabities in table, matching and fill in the blanks question types.
 */
export const getIsWorksheetDocEditorEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetDocEditorMigration");
};

/**
 * @description Checks if copy-paste restriction is enabled for students in browser lock exam mode.
 * @changes Prevents students from pasting content into input fields during worksheet attempts when browser lock (Restricted Access/Safe Exam Browser) is active.
 */
export const getIsStudentCanCopyPasteInWorksheet = () => {
  return ACLStore.can("FeatureFlag:DisableCopyPasteForLockedWorksheet");
};

/**
 * @description Checks if the V2 queries for worksheet operations in Toddle Library context are enabled.
 * @changes Enables V2 GraphQL queries specifically for worksheet operations within the Toddle Library portal.
 */
export const getIsToddleLibraryWorksheetQueriesV2Enabled = () => {
  return ACLStore.can("FeatureFlag:ToddleLibraryWorksheetQueriesV2");
};

/**
 * @description Checks if the AI question generation via Gemini API is enabled.
 * @changes Migrates AI question generation from previous provider to Gemini API with streaming support.
 */
export const getIsNewWorksheetAIStreamingEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetGeminiMigration");
};

/**
 * @description Checks if the video lag fix for worksheets is enabled.
 * @changes Implements a workaround to fix video lag issues in embedded videos within worksheets, improving playback performance.
 */
export const getIsWorksheetVideoLagFixEnabled = () => {
  return ACLStore.can("FeatureFlag:WorksheetVideoLagFix");
};

/**
 * @description Checks if we have to add category filter or not
 * @changes Add filter to ai prompt query based on feature flag
 */
export const getIsWorksheetAiAddCategoryFilter = () => {
  return ACLStore.can("FeatureFlag:WorksheetPromptFilters");
};
